import axios from "axios";
import { toast } from "sonner";
// PR TIER1-10 (2026-06-27) — fire doc_previewed every time a Preview path
// resolves. Centralising in this helper means every call site (Deal docs,
// Gmail attachments, lender docs, etc.) gets tracked without per-component
// wiring. Download path stays untracked here — we only care about previews
// for "broker workflow speed" analytics.
import { trackDocPreviewed } from "@/lib/analytics";

// PR Docs-2 (2026-05-28): mime types the browser can natively render
// inline in a new tab. Anything outside this list is forced into
// download mode regardless of `mode` arg — mobile Safari/Chrome can't
// preview .xlsx / .docx / .pptx, so window.open(blob) just opens an
// empty tab. The backend already returns Content-Disposition: attachment
// for these, but if the response Content-Disposition isn't readable
// (some CORS configurations strip it), this list is the fallback.
const INLINE_PREVIEWABLE_MIMES = new Set([
  "application/pdf",
  "application/json",
  "text/csv",
]);
function isInlinePreviewable(contentType) {
  if (!contentType) return false;
  const lower = contentType.toLowerCase().split(";")[0].trim();
  if (lower.startsWith("image/") || lower.startsWith("text/")) return true;
  return INLINE_PREVIEWABLE_MIMES.has(lower);
}

export const openProtectedFile = async ({ url, filename, mode = "preview" }) => {
  try {
    const response = await axios.get(url, { responseType: "blob" });
    const contentType = response.headers["content-type"] || "application/octet-stream";
    const blob = new Blob([response.data], { type: contentType });
    const objectUrl = window.URL.createObjectURL(blob);

    // PR Docs-2 — server hint trumps the caller's mode. If the response
    // is marked as attachment OR the mime isn't inline-previewable, force
    // download. Fixes mobile preview of Office files (.xlsx/.docx/.pptx)
    // which mobile browsers can't render in a popup tab.
    //
    // PR HF-Preview-1 (2026-06-01) — parse Content-Disposition properly.
    // The previous `cd.includes("attachment")` was a naive substring match
    // that returned true any time the filename contained the word
    // "attachment" (e.g. `inline; filename="attachment-abc.pdf"` looks like
    // an attachment disposition even though it's marked inline). Symptom:
    // Gmail attachment Preview downloaded the PDF instead of opening it in
    // the browser viewer. Fix: split on `;` and check only the first
    // segment (the actual disposition type), not the whole header.
    const cd = (response.headers["content-disposition"] || "").toLowerCase();
    const dispositionType = cd.split(";")[0].trim();
    const serverWantsAttachment = dispositionType === "attachment";
    const browserCanPreview = isInlinePreviewable(contentType);
    const effectiveMode = (mode === "download" || serverWantsAttachment || !browserCanPreview)
      ? "download"
      : "preview";

    if (effectiveMode === "download") {
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Downloading ${filename || "document"}...`);
    } else {
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      toast.success(`Opening ${filename || "document"}...`);
      // PR TIER1-10 — derive source from the URL path so we can tell
      // deal-doc previews apart from email-attachment / lender / lead
      // previews in PostHog without per-caller plumbing.
      let source = "unknown";
      try {
        const path = new URL(url, window.location.origin).pathname;
        if (path.includes("/applications/")) source = "deal";
        else if (path.includes("/leads/")) source = "lead";
        else if (path.includes("/gmail/") || path.includes("/email/")) source = "email";
        else if (path.includes("/envelopes/")) source = "envelope";
      } catch { /* noop */ }
      trackDocPreviewed({ fileName: filename, source });
    }

    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
  } catch (error) {
    toast.error(error.response?.data?.detail || `Failed to ${mode === "download" ? "download" : "open"} file`);
  }
};